import { Controller } from "@hotwired/stimulus";

export default class extends Controller<HTMLElement> {
    static targets = ["input", "menu", "message", "mirror", "option", "recipient"];

    declare readonly inputTarget: HTMLTextAreaElement;
    declare readonly menuTarget: HTMLElement;
    declare readonly messageTargets: HTMLElement[];
    declare readonly mirrorTarget: HTMLElement;
    declare readonly optionTargets: HTMLButtonElement[];
    declare readonly recipientTarget: HTMLInputElement;
    declare readonly hasInputTarget: boolean;
    declare readonly hasMirrorTarget: boolean;

    private activeIndex = -1;
    private connected = false;
    private highlightFrameId: number | null = null;
    private messageObserver: MutationObserver | null = null;
    private replacementRange: { start: number; end: number } | null = null;
    private selectedMention = "@elron";
    private refreshAfterUpdate = (event: Event): void => {
        const target = (event as CustomEvent).detail?.target;
        if (target instanceof Element && (target.contains(this.element) || this.element.contains(target))) {
            this.renderHighlights();
        }
    };

    connect(): void {
        this.connected = true;
        const selectedOption = this.optionTargets.find(
            (option) => option.dataset.recipientId === this.recipientTarget.value,
        );
        this.selectedMention = selectedOption?.dataset.mention || "@elron";
        if (selectedOption && this.inputTarget.value.startsWith(this.selectedMention)) {
            const caret = this.inputTarget.value.length;
            this.inputTarget.setSelectionRange(caret, caret);
        }
        this.renderHighlights();
        document.body.addEventListener("htmx:afterSwap", this.refreshAfterUpdate);
        document.addEventListener("ihp:auto-refresh-update", this.refreshAfterUpdate);
        this.messageObserver = new MutationObserver((mutations) => {
            const highlightsChanged = mutations.some((mutation) => {
                const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
                return Boolean(
                    target?.closest("#ticket-chat-messages"),
                );
            });
            if (!highlightsChanged) return;

            this.messageObserver?.disconnect();
            this.renderMessageHighlights();
            this.observeMessages();
        });
        this.observeMessages();
    }

    disconnect(): void {
        this.connected = false;
        document.body.removeEventListener("htmx:afterSwap", this.refreshAfterUpdate);
        document.removeEventListener("ihp:auto-refresh-update", this.refreshAfterUpdate);
        if (this.highlightFrameId !== null) {
            window.cancelAnimationFrame(this.highlightFrameId);
            this.highlightFrameId = null;
        }
        this.messageObserver?.disconnect();
    }

    messageTargetConnected(message: HTMLElement): void {
        this.highlightMessage(message, this.mentionLabels());
    }

    optionTargetConnected(): void {
        if (!this.connected || this.highlightFrameId !== null) return;

        this.highlightFrameId = window.requestAnimationFrame(() => {
            this.highlightFrameId = null;
            this.renderHighlights();
        });
    }

    input(): void {
        this.replacementRange = null;
        this.renderHighlights(false);

        if (this.recipientTarget.value !== "elron" && !this.inputTarget.value.includes(this.selectedMention)) {
            this.recipientTarget.value = "elron";
            this.selectedMention = "@elron";
        }

        const query = this.mentionQuery();
        if (query === null) {
            this.close();
            return;
        }

        const normalizedQuery = query.toLocaleLowerCase();
        const visibleOptions = this.optionTargets.filter((option) => {
            const visible = (option.dataset.mention || "").slice(1).toLocaleLowerCase().includes(normalizedQuery);
            option.hidden = !visible;
            return visible;
        });

        if (visibleOptions.length === 0) {
            this.close();
            return;
        }

        this.activeIndex = -1;
        this.menuTarget.hidden = false;
        this.inputTarget.setAttribute("aria-expanded", "true");
        this.highlight(visibleOptions);
    }

    click(): void {
        if (!this.hasMirrorTarget) return;

        const caret = this.inputTarget.selectionStart ?? -1;
        const mention = Array.from(
            this.mirrorTarget.querySelectorAll<HTMLElement>("[data-ticket-chat-highlighted-mention='true']"),
        ).find((element) => {
            const start = Number(element.dataset.mentionStart);
            const end = Number(element.dataset.mentionEnd);
            return caret >= start && caret <= end;
        });
        if (!mention) return;

        this.replacementRange = {
            start: Number(mention.dataset.mentionStart),
            end: Number(mention.dataset.mentionEnd),
        };
        this.optionTargets.forEach((option) => (option.hidden = false));
        this.activeIndex = Math.max(
            0,
            this.optionTargets.findIndex((option) => option.dataset.mention === mention.textContent),
        );
        this.menuTarget.hidden = false;
        this.inputTarget.setAttribute("aria-expanded", "true");
        this.highlight(this.optionTargets);
    }

    openPicker(event: Event): void {
        event.preventDefault();
        const caret = this.inputTarget.selectionStart ?? this.inputTarget.value.length;
        this.replacementRange = { start: caret, end: caret };
        this.optionTargets.forEach((option) => (option.hidden = false));
        this.activeIndex = -1;
        this.menuTarget.hidden = false;
        this.inputTarget.setAttribute("aria-expanded", "true");
        this.highlight(this.optionTargets);
        this.inputTarget.focus();
    }

    keydown(event: KeyboardEvent): void {
        if (this.menuTarget.hidden) return;

        const options = this.visibleOptions();
        if (event.key === "Escape") {
            event.preventDefault();
            this.close();
            return;
        }

        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            event.stopImmediatePropagation();
            const direction = event.key === "ArrowDown" ? 1 : -1;
            this.activeIndex = this.activeIndex < 0
                ? (direction > 0 ? 0 : options.length - 1)
                : (this.activeIndex + direction + options.length) % options.length;
            this.highlight(options);
            return;
        }

        if (event.key === "Enter") {
            event.preventDefault();
            event.stopImmediatePropagation();
            if (options[this.activeIndex]) this.choose(options[this.activeIndex]);
        }
    }

    select(event: Event): void {
        event.preventDefault();
        if (event.currentTarget instanceof HTMLButtonElement) this.choose(event.currentTarget);
    }

    keepFocus(event: Event): void {
        event.preventDefault();
    }

    closeLater(): void {
        window.setTimeout(() => this.close(), 100);
    }

    syncScroll(): void {
        if (!this.hasMirrorTarget) return;
        this.mirrorTarget.scrollTop = this.inputTarget.scrollTop;
        this.mirrorTarget.scrollLeft = this.inputTarget.scrollLeft;
    }

    private choose(option: HTMLButtonElement): void {
        const mention = option.dataset.mention;
        const recipientId = option.dataset.recipientId;
        const range = this.replacementRange ?? this.mentionRange();
        if (!mention || !recipientId || !range) return;

        const before = this.inputTarget.value.slice(0, range.start);
        const after = this.inputTarget.value.slice(range.end);
        this.inputTarget.value = `${before}${mention}${after.startsWith(" ") ? "" : " "}${after}`;
        const caret = before.length + mention.length + 1;
        this.inputTarget.setSelectionRange(caret, caret);
        this.recipientTarget.value = recipientId;
        this.selectedMention = mention;
        this.replacementRange = null;
        this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
        this.inputTarget.focus();
        this.close();
    }

    private mentionQuery(): string | null {
        const range = this.mentionRange();
        return range ? this.inputTarget.value.slice(range.start + 1, range.end) : null;
    }

    private mentionRange(): { start: number; end: number } | null {
        const end = this.inputTarget.selectionStart ?? this.inputTarget.value.length;
        const beforeCaret = this.inputTarget.value.slice(0, end);
        const start = beforeCaret.lastIndexOf("@");
        if (start < 0 || /\s/.test(beforeCaret.slice(start + 1))) return null;
        return { start, end };
    }

    private visibleOptions(): HTMLButtonElement[] {
        return this.optionTargets.filter((option) => !option.hidden);
    }

    private renderHighlights(includeMessages = true): void {
        const mentions = this.mentionLabels();

        if (this.hasInputTarget && this.hasMirrorTarget) {
            this.renderHighlightedText(this.mirrorTarget, this.inputTarget.value, mentions);
            if (this.inputTarget.value.length > 0) this.syncScroll();
        }
        if (includeMessages) this.renderMessageHighlights();
    }

    private renderMessageHighlights(): void {
        const mentions = this.mentionLabels();
        this.messageTargets.forEach((message) => this.highlightMessage(message, mentions));
    }

    private highlightMessage(message: HTMLElement, mentions: string[]): void {
        message.querySelectorAll<HTMLElement>("[data-ticket-chat-highlighted-mention]").forEach((highlight) => {
            highlight.replaceWith(document.createTextNode(highlight.textContent || ""));
        });
        message.normalize();
        const walker = document.createTreeWalker(message, NodeFilter.SHOW_TEXT);
        const nodes: Text[] = [];
        while (walker.nextNode()) {
            const node = walker.currentNode as Text;
            if (!node.parentElement?.closest("code, pre, script, style")) nodes.push(node);
        }
        for (const node of nodes) {
            if (!mentions.some((mention) => node.data.includes(mention))) continue;
            const highlighted = document.createElement("span");
            this.renderHighlightedText(highlighted, node.data, mentions);
            node.replaceWith(...Array.from(highlighted.childNodes));
        }
    }

    private observeMessages(): void {
        this.messageObserver?.observe(this.element, { childList: true, subtree: true });
    }

    private mentionLabels(): string[] {
        const labels = this.optionTargets
            .map((option) => option.dataset.mention)
            .filter(Boolean) as string[];
        // Older team messages address the assistant as @elron. The picker
        // label is @support, and both should highlight.
        if (labels.includes("@support") && !labels.includes("@elron")) {
            labels.push("@elron");
        }
        return [...new Set(labels)].sort((left, right) => right.length - left.length);
    }

    private renderHighlightedText(target: HTMLElement, value: string, mentions: string[]): void {
        const fragment = document.createDocumentFragment();
        let plainText = "";
        let index = 0;

        const appendPlainText = () => {
            if (plainText === "") return;
            fragment.append(document.createTextNode(plainText));
            plainText = "";
        };

        while (index < value.length) {
            const mention = mentions.find((candidate) => this.matchesMention(value, index, candidate));
            if (!mention) {
                plainText += value[index];
                index += 1;
                continue;
            }

            appendPlainText();
            const highlighted = document.createElement("span");
            highlighted.className = "ticket-chat-mention-highlight rounded px-0.5 font-semibold";
            highlighted.dataset.ticketChatHighlightedMention = "true";
            highlighted.dataset.mentionStart = String(index);
            highlighted.dataset.mentionEnd = String(index + mention.length);
            highlighted.textContent = mention;
            fragment.append(highlighted);
            index += mention.length;
        }

        appendPlainText();
        target.replaceChildren(fragment);
    }

    private matchesMention(value: string, index: number, mention: string): boolean {
        if ((index > 0 && !/\s/.test(value[index - 1])) || !value.startsWith(mention, index)) return false;
        const nextCharacter = value[index + mention.length];
        return nextCharacter === undefined || /[\s.,!?;:]/.test(nextCharacter);
    }

    private highlight(options: HTMLButtonElement[]): void {
        options.forEach((option, index) => {
            option.setAttribute("aria-selected", index === this.activeIndex ? "true" : "false");
            option.classList.toggle("bg-muted", index === this.activeIndex);
        });
    }

    private close(): void {
        this.menuTarget.hidden = true;
        this.inputTarget.setAttribute("aria-expanded", "false");
    }
}
